import type { Tenant } from '@/api/types';

/** BM-68: the board a platform staff member enters from the platform host (Identity's HandoffKind). */
export type HandoffKind = 'admin' | 'ledger';

export const HANDOFF_LABEL: Record<HandoffKind, string> = {
    admin: 'Open admin',
    ledger: 'Open Ledger',
};

export const NO_HOST: Record<HandoffKind, string> = {
    admin: 'No admin host yet',
    ledger: 'No Ledger host yet',
};

/** Identity opens the board on the tenant's host of that kind (its default domain, else the first one), so a tenant without one cannot be opened. */
export function hasHost(
    tenant: Pick<Tenant, 'domains'>,
    kind: HandoffKind,
): boolean {
    return tenant.domains.some((d) => d.kind === kind);
}

/**
 * The hand-off through the app server (`POST /auth/handoff`, the kit's route): Identity makes a session on the
 * tenant host and a one-time grant; the answer is where the browser goes to arrive signed in. Null when refused.
 * BM-109: with `ledger` and no tenant, the platform host opens the platform's own Ledger host (`ledger.<domain>`);
 * a tenant board passes its own tenant to open that tenant's Ledger board.
 */
export async function handoff(
    tenantId: string | null,
    kind: HandoffKind,
): Promise<string | null> {
    const r = await fetch('/auth/handoff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
            tenantId === null ? { kind } : { tenant_id: tenantId, kind },
        ),
    });
    if (!r.ok) return null;
    const body = (await r.json().catch(() => null)) as { url?: string } | null;
    return body?.url ?? null;
}
